import { useRef } from "react";
import { motion } from "framer-motion";
import { Package, ArrowRight } from "lucide-react";
import { useI18n } from "@/i18n/LanguageContext";

const EASE = [0.16, 1, 0.3, 1];

const ACCENTS = {
  geisha: "#B0682C",
  gourmet: "#1F3D2A",
  lavado: "#2E7D6B",
  honey: "#D9822B",
};

function VarietyCard({ variety, labels, index }) {
  const cardRef = useRef(null);
  const accent = ACCENTS[variety.id];

  const onMouseMove = (e) => {
    const rect = cardRef.current?.getBoundingClientRect();
    if (!rect) return;
    cardRef.current.style.setProperty("--mx", `${e.clientX - rect.left}px`);
    cardRef.current.style.setProperty("--my", `${e.clientY - rect.top}px`);
  };

  return (
    <motion.article
      ref={cardRef}
      onMouseMove={onMouseMove}
      data-testid={`variety-card-${variety.id}`}
      initial={{ opacity: 0, y: 48 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.9, delay: (index % 2) * 0.15, ease: EASE }}
      className="group relative overflow-hidden border border-pine/10 bg-paper p-6 shadow-sm transition-shadow duration-500 hover:shadow-xl hover:shadow-pine/10 sm:p-8"
    >
      <div
        className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-500 group-hover:opacity-100"
        style={{
          background: `radial-gradient(480px circle at var(--mx, 50%) var(--my, 50%), ${accent}18, transparent 65%)`,
        }}
      />
      <div className="absolute inset-x-0 top-0 h-1" style={{ background: accent }} />

      <div className="relative">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-sand">
              {variety.tag}
            </div>
            <h3 className="mt-2 font-serif text-3xl tracking-tight text-pine">
              Monte Yolli <span className="italic" style={{ color: accent }}>{variety.name}</span>
            </h3>
          </div>
          <div className="border border-pine/15 bg-cream px-3 py-1 font-mono text-[10px] tracking-[0.2em] text-pine">
            {variety.score}
          </div>
        </div>

        <p className="mt-4 text-sm font-light leading-relaxed text-sand">
          {variety.description}
        </p>

        <div className="mt-5 flex flex-wrap gap-2">
          {variety.notes.map((n) => (
            <span
              key={n}
              className="border border-pine/10 bg-cream px-3 py-1 text-xs font-light text-bark/80"
            >
              {n}
            </span>
          ))}
        </div>

        <div className="mt-6 grid grid-cols-3 gap-3 border-t border-pine/10 pt-4 font-mono text-[10px] uppercase tracking-[0.15em]">
          <div>
            <div className="text-sand">{labels.altitude}</div>
            <div className="mt-1 text-pine">{variety.altitude}</div>
          </div>
          <div>
            <div className="text-sand">{labels.process}</div>
            <div className="mt-1 text-pine">{variety.process}</div>
          </div>
          <div>
            <div className="text-sand">{labels.roast}</div>
            <div className="mt-1 normal-case text-pine">{variety.roast}</div>
          </div>
        </div>
      </div>
    </motion.article>
  );
}

export default function Varieties() {
  const { t } = useI18n();
  const p = t.products;

  return (
    <section id="varietes" className="mx-auto max-w-7xl px-4 py-24 sm:px-8 md:py-36">
      <motion.div
        initial={{ opacity: 0, y: 32 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-100px" }}
        transition={{ duration: 0.9, ease: EASE }}
        className="mb-16 max-w-2xl"
      >
        <div className="mb-4 font-mono text-xs uppercase tracking-[0.3em] text-copper">
          {p.eyebrow}
        </div>
        <h2 className="font-serif text-3xl leading-none tracking-tight text-pine sm:text-5xl">
          {p.titleA} <span className="italic text-copper">{p.titleEm}</span>
        </h2>
        <p className="mt-6 text-base font-light leading-relaxed text-sand sm:text-lg">
          {p.sub}
        </p>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 1, ease: EASE }}
        data-testid="product-spotlight"
        className="mb-20 grid items-center gap-12 border border-pine/10 bg-paper p-6 shadow-sm sm:p-10 lg:grid-cols-2"
      >
        <div
          className="bg-gradient-to-br from-copper/50 via-copper/10 to-pine/20 p-px"
          style={{ clipPath: "polygon(0 0, 100% 0, 100% 92%, 92% 100%, 0 100%)" }}
        >
          <div
            className="flex items-center justify-center bg-creamd p-8"
            style={{ clipPath: "polygon(0 0, 100% 0, 100% 92%, 92% 100%, 0 100%)" }}
          >
            <img
              src="/assets/bag-400g.png"
              alt="Sac Monte Yolli 400g — Café de Origen Micro Lote"
              loading="lazy"
              className="max-h-[440px] w-auto object-contain transition-transform duration-700 ease-out hover:scale-[1.03]"
            />
          </div>
        </div>

        <div>
          <div className="flex items-center gap-3 font-mono text-xs uppercase tracking-[0.3em] text-copper">
            <Package className="h-4 w-4" />
            {p.spotEyebrow}
          </div>
          <h3 className="mt-4 font-serif text-3xl tracking-tight text-pine sm:text-4xl">
            {p.spotTitle} — <span className="italic text-copper">400g</span>
          </h3>
          <p className="mt-5 text-base font-light leading-relaxed text-sand">
            {p.spotDesc}
          </p>
          <div className="mt-8 divide-y divide-pine/10 border-y border-pine/10">
            {p.specs.map(([label, value]) => (
              <div key={label} className="flex items-center justify-between gap-4 py-3">
                <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-sand">
                  {label}
                </span>
                <span className="text-right text-sm text-bark">{value}</span>
              </div>
            ))}
          </div>
          <a
            href="#contact"
            data-testid="product-cta-devis"
            className="group mt-8 inline-flex items-center gap-3 bg-pine px-7 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-cream transition-colors duration-300 hover:bg-copper"
          >
            {p.ctaDevis}
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </a>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 1, ease: EASE }}
        data-testid="product-lineup"
        className="relative mb-20 overflow-hidden border border-pine/10"
      >
        <img
          src="/assets/bags-lineup.png"
          alt="Gamme de sacs Monte Yolli 400g"
          loading="lazy"
          className="h-64 w-full object-cover sm:h-96"
        />
        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-pine/90 to-transparent p-6 sm:p-8">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-cream/80">
            {p.lineupEyebrow}
          </div>
          <div className="mt-2 font-serif text-xl text-cream sm:text-2xl">
            {p.lineupTitle}
          </div>
        </div>
      </motion.div>

      <div className="grid gap-6 md:grid-cols-2 md:gap-8">
        {t.varieties.map((v, i) => (
          <VarietyCard key={v.id} variety={v} labels={p.labels} index={i} />
        ))}
      </div>
    </section>
  );
}
